import React, { useState, useRef } from "react";
import { useEffect } from "react";
import { Box, Typography, Card, Container, useTheme, Button, Skeleton } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RebalancerAllocation from "./RebalancerDetails/RebalancerAllocation";
import { Helmet } from "react-helmet";
import { clearRebalancer } from "../../Redux/Reducers/rebalancerSlice";
import { useDispatch } from "react-redux";

const Rebalancer = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [showRestartButton, setShowRestartButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [rebalanceType, setRebalanceType] = useState("regular");
  const rebalancerRef = useRef();

  useEffect(() => {
    dispatch(clearRebalancer())
  }, []);

  const handleRestart = () => {
    setIsLoading(true);
    // Reset the rebalancer state
    dispatch(clearRebalancer());
    // Trigger restart in child component
    if (rebalancerRef.current && rebalancerRef.current.restart) {
      rebalancerRef.current.restart();
    }
    setShowRestartButton(false);
    // Simulate loading time for better UX
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const getStepInfo = (step, type) => {
    if (type === "swap") {
      switch (step) {
        case 12: return { number: 1, name: "Select Swap Contracts" };
        case 1: return { number: 1, name: "Review Execution Styles" };
        case 2: return { number: 2, name: "Review Validated Details" };
        case 3: return { number: 3, name: "Submit Swap Contracts" };
        default: return { number: 0, name: "Select Rebalance Type" };
      }
    } else if (type === "all") {
      switch (step) {
        case 0: return { number: 0, name: "Select Rebalance Type" };
        case 1: return { number: 1, name: "Blacklisted Securities" };
        case 2: return { number: 2, name: "Rebalance Preview" };
        case 6: return { number: 3, name: "Final Trades" };
        default: return { number: 0, name: "Select Rebalance Type" };
      }
    } else if (type === "specific") {
      switch (step) {
        case 0: return { number: 0, name: "Select Rebalance Type" };
        case 1: return { number: 1, name: "Blacklisted Securities" };
        case 2: return { number: 2, name: "Rebalance Preview" };
        case 3: return { number: 3, name: "Crossing Trades" };
        case 4: return { number: 4, name: "Locates Management" };
        case 5: return { number: 4, name: "Locates Management" };
        case 6: return { number: 5, name: "Final Trades" };
        default: return { number: 0, name: "Select Rebalance Type" };
      }
    } else {
      // Fallback for any other type (including "regular")
      switch (step) {
        case 0: return { number: 0, name: "Select Rebalance Type" };
        case 1: return { number: 1, name: "Blacklisted Securities" };
        case 2: return { number: 2, name: "Rebalance Preview" };
        case 3: return { number: 3, name: "Final Trades" };
        default: return { number: 0, name: "Select Rebalance Type" };
      }
    }
  };

  const handleStepChange = (step, type) => {
    setCurrentStep(step);
    setRebalanceType(type);
    if (step > 0) {
      setShowRestartButton(true);
    }
  };
  return (
    <>
      <Helmet>
        <title>Rebalancer | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "auto",
          py: 3,
          flex: "1 1 auto",
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                Rebalancer
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              >
                Configure and manage your rebalancing strategies
              </Typography>
              {currentStep > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  >
                    Step {getStepInfo(currentStep, rebalanceType).number}:
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    {getStepInfo(currentStep, rebalanceType).name}
                  </Typography>
                </Box>
              )}
            </Box>
            {showRestartButton && (
              <Box sx={{ ml: 2, mt: 0.5 }}>
                {isLoading ? (
                  <Skeleton
                    variant="rectangular"
                    width={180}
                    height={40}
                    sx={{ borderRadius: 1 }}
                  />
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={handleRestart}
                    sx={{
                      color: "primary.main",
                      borderColor: "primary.main",
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      "&:hover": {
                        backgroundColor: "primary.light",
                        color: "white",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    Start Over
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <RebalancerAllocation 
              ref={rebalancerRef}
              onStepChange={handleStepChange}
            />
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default Rebalancer;
